import { useState, useEffect } from 'react';
import { Mail, Calendar, Clock, KeyRound, UserCog } from '../../icons/wp-icons';
import { useAuth } from '../../context/AuthContext';

function MitarbeiterFormular({ benutzer, onSubmit, onCancel, isLoading, isSelfAdmin = false }) {
  const { user } = useAuth();
  const isHeykikiOrg = user?.organisation?.orgType === 'HEYKIKI';
  const productName = isHeykikiOrg ? 'HeyKiki' : 'WerkPilot';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    rolle: 'mitarbeiter',
    aktiv: true,
    loginAktiv: true,
    farbe: '',
    urlaubstageJahr: 28,
    urlaubstageRest: 0,
    stundensatz: 45,
    taetigkeitsbereich: '',
    autoZuweisungAktiv: false
  });
  const [errors, setErrors] = useState({});
  const isNewUser = !benutzer;
  const isOwner = !!benutzer?.isOrganisationOwner;
  const loginLocked = isOwner || isSelfAdmin; // Eigener Login darf nicht entfernt werden

  useEffect(() => {
    if (benutzer) {
      setFormData({
        name: benutzer.name || '',
        email: benutzer.email || '',
        rolle: benutzer.rolle || 'mitarbeiter',
        aktiv: benutzer.aktiv ?? true,
        loginAktiv: benutzer.loginAktiv ?? true,
        farbe: benutzer.farbe || '',
        urlaubstageJahr: benutzer.urlaubstageJahr ?? 28,
        urlaubstageRest: benutzer.urlaubstageRest ?? 0,
        stundensatz: benutzer.stundensatz ?? 45,
        taetigkeitsbereich: benutzer.taetigkeitsbereich || '',
        autoZuweisungAktiv: benutzer.autoZuweisungAktiv ?? false
      });
    }
  }, [benutzer]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Fehler beim Tippen entfernen
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    // E-Mail nur Pflicht wenn Login aktiv ist
    if (formData.loginAktiv) {
      if (!formData.email.trim()) {
        newErrors.email = 'E-Mail ist für Mitarbeiter mit Login-Zugang erforderlich';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Ungültiges E-Mail-Format';
      }
    } else if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      // Optional: wenn Email trotzdem eingegeben, muss sie valide sein
      newErrors.email = 'Ungültiges E-Mail-Format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) return;

    onSubmit(formData);
  };

  const mitarbeiterFarbPalette = [
    { value: '#057867', label: 'Petrol' },
    { value: '#10b981', label: 'Grün' },
    { value: '#166534', label: 'Dunkelgrün' },
    { value: '#06b6d4', label: 'Türkis' },
    { value: '#3b82f6', label: 'Blau' },
    { value: '#1e40af', label: 'Dunkelblau' },
    { value: '#8b5cf6', label: 'Violett' },
    { value: '#ec4899', label: 'Pink' },
    { value: '#ef4444', label: 'Rot' },
    { value: '#f59e0b', label: 'Orange' },
    { value: '#eab308', label: 'Gelb' },
    { value: '#6b7280', label: 'Grau' }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Login-Zugang Toggle — oben platziert, steuert die Sichtbarkeit der übrigen Felder */}
      <div className={`rounded-lg border p-4 ${formData.loginAktiv ? 'bg-primary/5 border-primary/20' : 'bg-gray-50 border-border'}`}>
        <label className={`flex items-start gap-3 ${loginLocked ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'}`}>
          <input
            type="checkbox"
            name="loginAktiv"
            checked={formData.loginAktiv}
            onChange={handleChange}
            disabled={loginLocked}
            className="w-4 h-4 mt-0.5 rounded text-primary"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-primary" />
              <span className="font-medium text-sm">Login-Zugang zu {productName}</span>
            </div>
            <p className="text-xs text-text-muted mt-1">
              {formData.loginAktiv
                ? 'Mitarbeiter erhält eine E-Mail-Einladung und kann sich einloggen. Zählt gegen dein Plan-Limit.'
                : 'Nur Stammdaten — Mitarbeiter kann Anfragen/Terminen zugewiesen werden, hat aber keinen Zugang. Unbegrenzt verfügbar.'}
            </p>
            {loginLocked && (
              <p className="text-amber-600 text-xs mt-1">
                {isOwner ? 'Der Organisations-Inhaber muss Login-Zugang behalten' : 'Sie können Ihren eigenen Login-Zugang nicht entfernen'}
              </p>
            )}
          </div>
        </label>
      </div>

      {/* Name */}
      <div>
        <label className="block text-sm font-medium mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.name ? 'border-red-500' : 'border-border'
          }`}
          placeholder="Vor- und Nachname"
        />
        {errors.name && (
          <p className="text-red-500 text-xs mt-1">{errors.name}</p>
        )}
      </div>

      {/* E-Mail */}
      <div>
        <label className="block text-sm font-medium mb-1">
          E-Mail {formData.loginAktiv && <span className="text-red-500">*</span>}
          {!formData.loginAktiv && <span className="text-text-muted text-xs ml-1">(optional)</span>}
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.email ? 'border-red-500' : 'border-border'
          }`}
          placeholder="email@beispiel.de"
        />
        {errors.email && (
          <p className="text-red-500 text-xs mt-1">{errors.email}</p>
        )}
      </div>

      {/* Einladungs-Info für neue Benutzer — nur wenn Login aktiv */}
      {isNewUser && formData.loginAktiv && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-800">
                Einladung per E-Mail
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Der neue Mitarbeiter erhält eine E-Mail mit einem Link, um sein eigenes Passwort festzulegen.
                Der Link ist 7 Tage gültig.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stammdaten-Hinweis für neue Mitarbeiter ohne Login */}
      {isNewUser && !formData.loginAktiv && (
        <div className="bg-gray-50 border border-border rounded-lg p-4">
          <div className="flex items-start gap-3">
            <UserCog className="w-5 h-5 text-text-muted mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-gray-800">Nur Stammdaten</p>
              <p className="text-sm text-text-muted mt-1">
                Dieser Mitarbeiter wird angelegt, erhält aber keinen Zugang. Du kannst ihn jederzeit bearbeiten und den Login-Zugang nachträglich aktivieren.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Rolle — nur bei Login-fähigen Mitarbeitern */}
      {formData.loginAktiv && (
      <div>
        <label className="block text-sm font-medium mb-2">Rolle</label>
        <div className="flex gap-4">
          <label className={`flex items-center gap-2 ${isSelfAdmin ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
            <input
              type="radio"
              name="rolle"
              value="mitarbeiter"
              checked={formData.rolle === 'mitarbeiter'}
              onChange={handleChange}
              disabled={isSelfAdmin}
              className="w-4 h-4 text-primary"
            />
            <span>Mitarbeiter</span>
          </label>
          <label className={`flex items-center gap-2 ${isSelfAdmin ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
            <input
              type="radio"
              name="rolle"
              value="admin"
              checked={formData.rolle === 'admin'}
              onChange={handleChange}
              disabled={isSelfAdmin}
              className="w-4 h-4 text-primary"
            />
            <span>Admin</span>
          </label>
        </div>
        {isSelfAdmin ? (
          <p className="text-amber-600 text-xs mt-1">
            Sie können Ihre eigene Admin-Rolle nicht ändern
          </p>
        ) : (
          <p className="text-text-muted text-xs mt-1">
            Admins haben automatisch Vollzugriff auf alle Module
          </p>
        )}
      </div>
      )}

      {/* Aktiv */}
      <div>
        <label className={`flex items-center gap-2 ${isSelfAdmin ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
          <input
            type="checkbox"
            name="aktiv"
            checked={formData.aktiv}
            onChange={handleChange}
            disabled={isSelfAdmin}
            className={`w-4 h-4 rounded ${isSelfAdmin ? 'text-gray-300 bg-gray-100 border-gray-300' : 'text-primary'}`}
          />
          <span className={`font-medium ${isSelfAdmin ? 'text-gray-400' : ''}`}>Konto aktiv</span>
        </label>
        {isSelfAdmin ? (
          <p className="text-amber-600 text-xs mt-1 ml-6">
            Sie können Ihr eigenes Admin-Konto nicht deaktivieren
          </p>
        ) : (
          <p className="text-text-muted text-xs mt-1 ml-6">
            Inaktive Konten können sich nicht einloggen
          </p>
        )}
      </div>

      {/* Kalender-Farbe */}
      <div>
        <label className="block text-sm font-medium mb-2">Kalender-Farbe (optional)</label>
        <div className="flex flex-wrap items-center gap-2">
          {mitarbeiterFarbPalette.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, farbe: opt.value }))}
              className={`w-8 h-8 rounded-full border-2 transition shrink-0 ${
                formData.farbe === opt.value ? 'border-gray-800 scale-110' : 'border-transparent hover:scale-105'
              }`}
              style={{ backgroundColor: opt.value }}
              title={opt.label}
            />
          ))}
          {formData.farbe && (
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, farbe: '' }))}
              className="ml-1 px-3 py-1.5 text-xs text-text-muted hover:text-red-600 border border-border rounded-lg hover:bg-red-50 transition"
              title="Farbe entfernen"
            >
              Zurücksetzen
            </button>
          )}
        </div>

        {/* RGB-Picker + HEX-Eingabe */}
        <div className="flex items-center gap-3 mt-3">
          <input
            type="color"
            value={formData.farbe || '#057867'}
            onChange={(e) => setFormData(prev => ({ ...prev, farbe: e.target.value }))}
            className="w-12 h-12 rounded border border-border cursor-pointer shrink-0"
            title="Eigene Farbe wählen"
          />
          <div className="flex-1">
            <label className="block text-xs text-text-muted mb-1">HEX-Code</label>
            <input
              type="text"
              value={formData.farbe}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || /^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                  setFormData(prev => ({ ...prev, farbe: val }));
                }
              }}
              placeholder="#057867"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
            />
          </div>
        </div>

        <p className="text-text-muted text-xs mt-2">
          Wird als Rand um Termine dieses Mitarbeiters angezeigt. Leer lassen = Standard-Farbe des Termins.
        </p>
      </div>

      {/* Urlaubstage & Stundensatz - nur bei existierenden Benutzern */}
      {!isNewUser && (
        <div className="border-t border-border pt-4 mt-4">
          <h3 className="text-sm font-medium text-text-muted mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Arbeitszeit & Urlaub
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">
                Urlaubstage/Jahr
              </label>
              <input
                type="number"
                name="urlaubstageJahr"
                value={formData.urlaubstageJahr}
                onChange={handleChange}
                min="0"
                max="60"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Resturlaub
              </label>
              <input
                type="number"
                name="urlaubstageRest"
                value={formData.urlaubstageRest}
                onChange={handleChange}
                min="0"
                max="60"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Stundensatz
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="stundensatz"
                  value={formData.stundensatz}
                  onChange={handleChange}
                  min="0"
                  step="0.5"
                  className="w-full px-3 py-2 pr-8 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted text-sm">€</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auto-Zuweisung: Tätigkeitsbereich für KI-basiertes Routing eingehender Anfragen */}
      {!isNewUser && (
        <div className="border-t border-border pt-4 mt-4">
          <h3 className="text-sm font-medium text-text-muted mb-3 flex items-center gap-2">
            <UserCog className="w-4 h-4" />
            Automatische Anfrage-Zuweisung
          </h3>
          <div>
            <label className="block text-sm font-medium mb-1">
              Tätigkeitsbereich
            </label>
            <textarea
              name="taetigkeitsbereich"
              value={formData.taetigkeitsbereich}
              onChange={handleChange}
              rows={2}
              placeholder="z.B. Heizung, Sanitär, Solar"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-xs text-text-muted mt-1">
              Freitext. Wird vom KI-Telefonassistenten nach jedem Anruf genutzt, um die passende Anfrage automatisch zuzuweisen.
            </p>
          </div>
          <label className="flex items-start gap-3 mt-3 cursor-pointer">
            <input
              type="checkbox"
              name="autoZuweisungAktiv"
              checked={formData.autoZuweisungAktiv}
              onChange={handleChange}
              className="mt-0.5 w-4 h-4 rounded border-border text-primary focus:ring-primary"
            />
            <span className="text-sm">
              <span className="font-medium">Automatische Zuweisung aktivieren</span>
              <span className="block text-xs text-text-muted">
                Wenn das Anliegen klar zum Tätigkeitsbereich passt, wird der Mitarbeiter direkt eingetragen — sonst bleibt die Anfrage unzugewiesen.
              </span>
            </span>
          </label>
        </div>
      )}

      {/* Buttons */}
      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2 border border-border rounded-button hover:bg-gray-50 transition"
        >
          Abbrechen
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 px-4 py-2 bg-primary text-white rounded-button hover:bg-primary-dark transition disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isLoading ? (
            'Speichern...'
          ) : isNewUser ? (
            formData.loginAktiv ? (
              <>
                <Mail className="w-4 h-4" />
                Einladen
              </>
            ) : (
              <>
                <UserCog className="w-4 h-4" />
                Anlegen
              </>
            )
          ) : (
            'Aktualisieren'
          )}
        </button>
      </div>
    </form>
  );
}

export default MitarbeiterFormular;
